import type {
  AppState,
  DriveProfileKey,
  ConnectionMode,
  Alarms,
  Warnings,
  StateKey,
  StateListener,
} from '../types';

const DEFAULT_STATE: AppState = {
  selectedProfileKey: 'G130',
  connectionMode: 'sim',
  slaveId: 1,
  targetFreq: 50.0,
  load: 50,
  voltageDrop: 0,
  imbalance: 0,
  fanMainHours: 15000,
  fanControlHours: 8000,
  daysOffline: 0,
  looseTerminal: false,
  cloggedHeatsink: false,
  activeTrip: null,
  currentFreq: 0.0,
  currentAmp: 0.0,
  dcVoltage: 560,
  heatsinkTemp: 30.0,
  registers: {},
  alarms: {
    alarm13: false,
    alarm7: false,
    alarm8: false,
    alarm29: false,
  },
  warnings: {
    warn4: false,
    warn7: false,
    warn8: false,
    warn13: false,
    warn25: false,
  },
};

export class StateManager {
  private state: AppState;
  private listeners: Map<string, Set<StateListener<unknown>>> = new Map();

  constructor(initial?: Partial<AppState>) {
    this.state = { ...DEFAULT_STATE, ...initial };
  }

  get<K extends StateKey>(key: K): AppState[K] {
    return this.state[key];
  }

  set<K extends StateKey>(key: K, value: AppState[K]): void {
    this.state[key] = value;
    this.notify(key, value);
  }

  subscribe<K extends StateKey>(
    key: K,
    callback: StateListener<AppState[K]>,
  ): () => void {
    if (!this.listeners.has(key)) {
      this.listeners.set(key, new Set());
    }
    this.listeners.get(key)!.add(callback as StateListener<unknown>);

    return () => {
      this.listeners.get(key)?.delete(callback as StateListener<unknown>);
    };
  }

  private notify<K extends StateKey>(key: K, value: AppState[K]): void {
    this.listeners.get(key)?.forEach((cb) => cb(value));
  }

  getSnapshot(): AppState {
    return { ...this.state };
  }

  loadSnapshot(snapshot: AppState): void {
    Object.assign(this.state, snapshot);
    const keys = Object.keys(snapshot) as StateKey[];
    for (const key of keys) {
      this.listeners.get(key)?.forEach((cb) => {
        (cb as (v: unknown) => void)(snapshot[key]);
      });
    }
  }

  updateRegisters(newRegs: Record<number, number>): void {
    this.state.registers = { ...this.state.registers, ...newRegs };
    this.notify('registers', this.state.registers);
  }

  setAlarm(key: keyof Alarms, value: boolean): void {
    this.state.alarms[key] = value;
    this.notify('alarms', this.state.alarms);
  }

  setWarning(key: keyof Warnings, value: boolean): void {
    this.state.warnings[key] = value;
    this.notify('warnings', this.state.warnings);
  }

  setProfile(key: DriveProfileKey): void {
    this.state.selectedProfileKey = key;
    this.notify('selectedProfileKey', key);
  }

  setConnectionMode(mode: ConnectionMode): void {
    this.state.connectionMode = mode;
    this.notify('connectionMode', mode);
  }

  reset(): void {
    this.loadSnapshot(DEFAULT_STATE);
  }
}
